import { HttpInterceptorFn, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, BehaviorSubject, filter, take } from 'rxjs';
import { TokenService } from '../services/token.service';
import { AuthService } from '../services/api/auth.service';

let isRefreshing = false;
let refreshSubject = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);

  if (req.url.includes('/account/login') ||
    req.url.includes('/account/register') ||
    req.url.includes('/account/refresh') ||
    req.url.includes('/account/email-exists') ||
    req.url.includes('/account/confirm-email') ||
    req.url.includes('/account/password/reset') ||
    req.url.includes('/account/password/confirmation')) {
    return next(req);
  }

  const isLogout = req.url.includes('account/logout');

  const accessToken = tokenService.getAccessToken();

  if (!accessToken) {
    return next(req);
  }

  if (tokenService.isTokenAboutExpired() && !isLogout) {
    if (isRefreshing) {
      return refreshSubject.pipe(
        filter(result => result !== null),
        take(1),
        switchMap(() => {
          const newToken = tokenService.getAccessToken();
          const authReq = req.clone({
            setHeaders: {
              'Authorization': `Bearer ${newToken}`,
            }
          });
          return next(authReq);
        })
      );
    }

    isRefreshing = true;
    refreshSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap(() => {
        isRefreshing = false;
        refreshSubject.next(true);

        const newToken = tokenService.getAccessToken();
        const authReq = req.clone({
          setHeaders: {
            'Authorization': `Bearer ${newToken}`,
          }
        });
        return next(authReq);
      }),
      catchError((error: HttpErrorResponse) => {
        isRefreshing = false;
        refreshSubject.next(false);
        tokenService.logout();
        return throwError(() => error);
      })
    );
  }

  const authReq = req.clone({
    setHeaders: {
      'Authorization': `Bearer ${accessToken}`,
    }
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === HttpStatusCode.Unauthorized && tokenService.getRefreshToken() && !isLogout) {
        if (isRefreshing) {
          return refreshSubject.pipe(
            filter(result => result !== null),
            take(1),
            switchMap(() => {
              const newToken = tokenService.getAccessToken();
              const retryReq = req.clone({
                setHeaders: {
                  'Authorization': `Bearer ${newToken}`,
                }
              });
              return next(retryReq);
            })
          );
        }

        isRefreshing = true;
        refreshSubject.next(null);

        return authService.refreshToken().pipe(
          switchMap(() => {
            isRefreshing = false;
            refreshSubject.next(true);

            const newToken = tokenService.getAccessToken();
            const retryReq = req.clone({
              setHeaders: {
                'Authorization': `Bearer ${newToken}`,
              }
            });
            return next(retryReq);
          }),
          catchError((refreshError: HttpErrorResponse) => {
            isRefreshing = false;
            refreshSubject.next(false);
            tokenService.logout();
            return throwError(() => refreshError);
          })
        );
      }

      return throwError(() => error);
    })
  );
};
